import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check
    // AuthCallback will exchange the session_id and establish the session first
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Keep the dashboard in sync with the real subscription: refetch on plan change events, tab focus and every 5 minutes
  useEffect(() => {
    const onUpdated = () => checkAuth();
    const onVisible = () => { if (document.visibilityState === 'visible') checkAuth(); };
    window.addEventListener('bc-subscription-updated', onUpdated);
    document.addEventListener('visibilitychange', onVisible);
    const interval = setInterval(checkAuth, 5 * 60 * 1000);
    return () => {
      window.removeEventListener('bc-subscription-updated', onUpdated);
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(interval);
    };
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(response.data);
    return response.data;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData, { withCredentials: true });
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
  };

  const exchangeSession = async (sessionId) => {
    const response = await axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true });
    setUser(response.data);
    return response.data;
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const switchRole = async (newRole) => {
    const response = await axios.get(`${API}/users/me/role/${newRole}`, { withCredentials: true });
    setUser(response.data);
    return response.data;
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    exchangeSession,
    updateUser,
    switchRole,
    checkAuth,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isCompany: user?.user_type === 'company' || user?.user_type === 'both' || user?.role === 'admin',
    isContractor: user?.user_type === 'contractor' || user?.user_type === 'both',
    isPremium: user?.subscription_plan === 'premium' || user?.role === 'admin',
    refreshUser: checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
